import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/shared/field';
import { LoadingState } from '@/components/shared/states';
import { api, errorMessage } from '@/lib/api';
import { meKey } from '@/lib/session';
import { TermsConsentField } from '@/features/legal/terms-consent';
import { AuthLayout } from './auth-layout';
import { FormError } from './form-error';

interface Preview {
  email: string;
  organizationName: string;
  roleName: string;
  userExists: boolean;
}

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const preview = useQuery({ queryKey: ['invite', token], queryFn: () => api.post<Preview>('/auth/invitations/preview', { token }), enabled: !!token, retry: false });
  const form = useForm<{ name?: string; password: string; acceptTerms?: boolean }>();
  const accept = useMutation({
    mutationFn: (v: { name?: string; password: string }) => api.post('/auth/invitations/accept', { token, ...v, acceptTerms: true }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: meKey });
      navigate('/app', { replace: true });
    },
    onError: (e) => setError(errorMessage(e)),
  });

  if (preview.isLoading) return <LoadingState />;
  if (!token || preview.error) {
    return (
      <AuthLayout title="Convite">
        <FormError message={preview.error ? errorMessage(preview.error) : 'Link de convite incompleto.'} />
      </AuthLayout>
    );
  }
  const p = preview.data!;
  return (
    <AuthLayout title={`Entrar na equipe ${p.organizationName}`} subtitle={`Você foi convidado como ${p.roleName}, com o e-mail ${p.email}.`}>
      <form onSubmit={form.handleSubmit((v) => accept.mutate(v))} className="grid gap-4" noValidate>
        <FormError message={error} />
        {!p.userExists && (
          <Field id="name" label="Seu nome">
            <Input id="name" autoComplete="name" required minLength={2} {...form.register('name', { required: true })} />
          </Field>
        )}
        <Field id="password" label={p.userExists ? 'Sua senha atual' : 'Crie uma senha'} hint={p.userExists ? 'Você já tem conta: confirme a senha para aceitar.' : 'Mínimo de 8 caracteres, com letras e números.'}>
          <Input id="password" type="password" autoComplete={p.userExists ? 'current-password' : 'new-password'} {...form.register('password', { required: true })} />
        </Field>
        <TermsConsentField
          inputProps={form.register('acceptTerms', { required: 'É preciso aceitar os Termos de Uso e a Política de Privacidade' })}
          error={form.formState.errors.acceptTerms?.message}
        />
        <Button type="submit" size="lg" disabled={accept.isPending}>
          Aceitar convite
        </Button>
      </form>
    </AuthLayout>
  );
}
